import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Skeleton,
  Spinner,
  Text,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { z } from 'zod';

import {
  date as commonDate,
  finleyAssert,
  finleyAssertUnreachable,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { AssignmentPreviewJobFragmentGQLType } from '../../../../../../types/dashboard-core-gqlTypes.generated.d';
import { EditAssignmentDropdown } from '../../EditAssignmentDropdown';
import { AssignmentSummary } from './AssignmentSummary';
import { PreviewJobsTable } from './PreviewJobsTable';
import { colors } from 'packages/common-frontend/colors';
import { Alert } from 'packages/common-frontend/components/Alert';
import { useLatestAssignmentPipelineRunMetadataForImplementationContext } from 'packages/loanProduct/hooks/LatestAssignmentPipelineRunMetadataForImplementationProvider';
import { usePollAssignmentJobs } from 'packages/loanProduct/hooks/usePollAssignmentJobs';
import { AssignmentSummaryConfirmButton } from 'packages/loanProduct/ui/AssignmentSummaryConfirmButton';
import {
  AssignmentHeader,
  AssignmentTemplate,
} from 'packages/loanProduct/ui/AssignmentTemplate';
import { PipelineStatusAlert } from 'packages/loanProduct/ui/v2/PipelineStatusAlert';
import { useQueryParams } from '~/packages/common-frontend/hooks/useQueryParams';

const AssignmentSummaryLoadingTemplate = () => {
  return (
    <AssignmentTemplate
      headerChildren={
        <HStack spacing="32px">
          <EditAssignmentDropdown isDisabled />
          <Button variant="solid" colorScheme="aurora" isDisabled>
            Confirm Assignments
          </Button>
        </HStack>
      }
    >
      <Skeleton data-testid="loading-assignment-summary-body" height="80vh" />
    </AssignmentTemplate>
  );
};

export const AssignmentDashboard = ({
  implementationId,
  reportingDate,
  fileVersion,
}: {
  implementationId: string;
  reportingDate: Util.FullDateStr;
  fileVersion: number;
}) => {
  const query = useQueryParams();
  const queryParamPreviewJobId = query.get('previewJobId');

  const [
    selectedPreviewJob,
    setSelectedPreviewJob,
  ] = useState<AssignmentPreviewJobFragmentGQLType | null>(null);

  const [
    initialPreviewJob,
    setInitialPreviewJob,
  ] = useState<AssignmentPreviewJobFragmentGQLType | null>(null);

  const [
    initiatingAssignmentConfirmationJob,
    setInitiatingAssignmentConfirmationJob,
  ] = useState<boolean>(false);

  const {
    previewJobs,
    latestAssignmentConfirmationJob,
  } = usePollAssignmentJobs({
    reportingDate,
    fileVersion,
    implementationId,
  });

  useEffect(() => {
    setInitiatingAssignmentConfirmationJob(false);
  }, [latestAssignmentConfirmationJob]);

  useEffect(() => {
    setSelectedPreviewJob(null);
    setInitialPreviewJob(null);
  }, [reportingDate, fileVersion]);

  useEffect(() => {
    if (previewJobs && previewJobs.length > 0) {
      if (selectedPreviewJob) {
        const currentJob = previewJobs.find(
          (job) => job.id === selectedPreviewJob.id,
        );
        setSelectedPreviewJob(currentJob ?? previewJobs[0]);
      } else if (queryParamPreviewJobId) {
        const queryParamJob = previewJobs.find(
          (job) => job.id === queryParamPreviewJobId,
        );
        setSelectedPreviewJob(queryParamJob ?? previewJobs[0]);
      } else {
        setSelectedPreviewJob(previewJobs[0]);
      }

      if (!initialPreviewJob) {
        setInitialPreviewJob(previewJobs[previewJobs.length - 1]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewJobs, queryParamPreviewJobId]);

  if (!selectedPreviewJob || !previewJobs) {
    return <AssignmentSummaryLoadingTemplate />;
  }

  const generatePreviewTitle = () => {
    const assignmentMethod = selectedPreviewJob.assignmentMethod;
    const createdAt = commonDate.epochToLocaleDateString(
      selectedPreviewJob.created_at,
    );
    let methodText: string;
    switch (assignmentMethod) {
      case 'assignNone':
        methodText = 'Assign None';
        break;
      case 'csv':
        methodText = 'Assign by CSV';
        break;
      case 'strategy':
        // eslint-disable-next-line no-case-declarations
        const strategyName = selectedPreviewJob.strategyName;
        finleyAssert(
          strategyName !== undefined && typeof strategyName === 'string',
          `Expected preview job ${selectedPreviewJob.id} to have a defined associated assignment strategy name`,
        );
        methodText = strategyName;
        break;
      default:
        return finleyAssertUnreachable(assignmentMethod);
    }
    return `${methodText} - ${createdAt}`;
  };

  const showAssignmentConfirmationJobInProgressAlert =
    initiatingAssignmentConfirmationJob ||
    (latestAssignmentConfirmationJob &&
      latestAssignmentConfirmationJob.status !== 'error');

  const showAssignmentConfirmationJobErrorAlert =
    !showAssignmentConfirmationJobInProgressAlert &&
    latestAssignmentConfirmationJob &&
    latestAssignmentConfirmationJob.status === 'error';

  return (
    <Box height="100%">
      <Box pb="1.5rem" background={colors.white}>
        <AssignmentHeader editMode={false}>
          <HStack spacing="32px" mb="1rem">
            <EditAssignmentDropdown
              implementationId={implementationId}
              isDisabled={
                initiatingAssignmentConfirmationJob ||
                latestAssignmentConfirmationJob?.status === 'inProgress'
              }
            />
            {selectedPreviewJob &&
            selectedPreviewJob.previewIdentifier &&
            selectedPreviewJob.status === 'completed' ? (
              <AssignmentSummaryConfirmButton
                implementationId={implementationId}
                previewIdentifier={selectedPreviewJob.previewIdentifier}
                receivableResponseTapePreviewJobId={selectedPreviewJob.id}
                reportingDate={reportingDate}
                fileVersion={fileVersion}
                setInitiatingAssignmentConfirmationJob={
                  setInitiatingAssignmentConfirmationJob
                }
                isDisabled={
                  initiatingAssignmentConfirmationJob ||
                  latestAssignmentConfirmationJob?.status === 'inProgress'
                }
              />
            ) : (
              <Button variant="solid" colorScheme="aurora" isDisabled>
                Confirm Assignments
              </Button>
            )}
          </HStack>
        </AssignmentHeader>
        {showAssignmentConfirmationJobErrorAlert && (
          <Alert margin="8px 0px 0px" status="error" isClosable>
            <Text>
              The last attempt to confirm assignments resulted in an error.
              Please try again, and contact your Finley representative if this
              persists.
            </Text>
          </Alert>
        )}
        {showAssignmentConfirmationJobInProgressAlert && (
          <Alert margin="8px 0px 0px" status="warning" isClosable={false}>
            <Text>
              Your latest assignments are being confirmed. You will be notified
              by email on completion.
            </Text>
          </Alert>
        )}
      </Box>
      {!showAssignmentConfirmationJobInProgressAlert && (
        <>
          <Box>
            <PreviewJobsTable
              selectedPreviewJob={selectedPreviewJob}
              setSelectedPreviewJob={setSelectedPreviewJob}
              previewJobs={previewJobs}
              queryParamPreviewJobId={queryParamPreviewJobId}
            />
          </Box>
          <Heading size="md" mt="32px">
            {generatePreviewTitle()}
          </Heading>
          {selectedPreviewJob.status !== 'completed' && (
            <Box p="32px" mt="2rem" bg={colors.millet[50]}>
              {selectedPreviewJob.status === 'error' && (
                <Alert status="error" isClosable={false}>
                  <Text>
                    {`An error occurred while generating assignment preview. Review your
            CSV or assignment strategy and try again.`}
                  </Text>
                </Alert>
              )}
              {selectedPreviewJob.status === 'inProgress' && (
                <Flex>
                  <Spinner />
                  <Heading ml="16px" size="md">
                    Preview generation in progress
                  </Heading>
                </Flex>
              )}
            </Box>
          )}
          {initialPreviewJob && selectedPreviewJob.status === 'completed' && (
            <AssignmentSummary
              reportingDate={reportingDate}
              fileVersion={fileVersion}
              initialPreviewJobId={initialPreviewJob.id}
              proposedPreviewJobId={selectedPreviewJob.id}
            />
          )}
        </>
      )}
    </Box>
  );
};

// Types
const RouteParamsStruct = z.object({
  implementationId: z.string({ message: 'Missing URL parameter' }),
});

export const AssignmentDashboardContainer = () => {
  const params = useParams();
  const { implementationId } = RouteParamsStruct.parse(params);

  const {
    metadata,
  } = useLatestAssignmentPipelineRunMetadataForImplementationContext();

  return (
    <Box height="100%" w="100%">
      <Box height="100%">
        {(() => {
          if (metadata === undefined) {
            return <AssignmentSummaryLoadingTemplate />;
          }
          if (!metadata || metadata.status !== 'success') {
            return (
              <AssignmentTemplate
                headerChildren={
                  <HStack spacing="32px">
                    <EditAssignmentDropdown isDisabled />
                    <Button variant="solid" colorScheme="aurora" isDisabled>
                      Confirm Assignments
                    </Button>
                  </HStack>
                }
              >
                <PipelineStatusAlert
                  status={metadata ? metadata.status : 'noPipelineRun'}
                />
              </AssignmentTemplate>
            );
          }
          return (
            <AssignmentDashboard
              implementationId={implementationId}
              fileVersion={metadata.fileVersion}
              reportingDate={metadata.reportingDate}
            />
          );
        })()}
      </Box>
    </Box>
  );
};
